import { WebDashboardContentSpec, WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { AIToolCall, FeatureFlag, WebDashboardContentConfig } from "@coeff/api";
import * as Sentry from "@sentry/react";
import { useFeatureGate } from "@statsig/react-bindings";
import moment from "moment";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { message as antMessage } from "../../components";

import { AiDashboardsHelpVideoCard } from "./AiDashboardsHelpVideoCard";
import {
  dismissAiDashboardsVideoPromo,
  useAiDashboardsVideoPromoDismissed,
} from "./aiDashboardsVideoPromoSync";
import { resolveBlockTitle } from "./BlockAutocompleteDropdown";
import { ChatInput, ChatInputHandle } from "./ChatInput";
import { ChatThemeRequestCard } from "./ChatThemeRequestCard";
import { ChatThemeRequestReceipt } from "./ChatThemeRequestReceipt";
import { DashboardWindow } from "./dashboardHelpers";
import { getAiDashboardsHelpVideoUrl } from "./dashboardHelpVideo";
import { DashboardHelpVideoModal } from "./DashboardHelpVideoModal";
import { MessageComponent } from "./MessageComponent";
import { getMessageText, hasDashboardModification, isUserMessage } from "./messageUtils";
import { RevertedMessageGroup } from "./RevertedMessageGroup";
import { computeRevertedMessageIds } from "./revertUtils";
import {
  ThemeHistorySnapshot,
  isFreshThemeMessage,
  nextThemeHistorySnapshot,
} from "./themeHistorySnapshot";
import {
  appendThemeRequestReceipt,
  readThemeRequestReceipts,
  removeThemeRequestReceipt,
  ThemeRequestReceipt,
} from "./themeRequestReceipts";
import { ThinkingLoader, getBuilderCompletedStepLabels } from "./ThinkingLoader";
import { computeTurnRevertUiByDisplayMessageId } from "./turnRevertUi";
import { useBlockPicker } from "./useBlockPicker";
import {
  ChatMode,
  DashboardArchivedError,
  Message,
  NeedsGoogleOAuthError,
  useChatMessages,
} from "./useChatMessages";
import { useDashboardAutoFix } from "./useDashboardAutoFix";
import { UserMemoryDrawer } from "./UserMemoryDrawer";
import { usePostAgentThreadPauseMutation } from "./webDashboard";

export type { ChatMode } from "./useChatMessages";

type OAuthProvider = "google" | "microsoft";

/** Props specific to super admin history mode */
export interface SuperAdminHistoryProps {
  /** Callback to load a specific dashboard version from S3 */
  onLoadVersion: (s3Url: string, timestamp: Date) => void;
  /** Whether a version is currently being loaded */
  isLoadingVersion: boolean;
}

export interface BlockContext {
  blockId: string;
  blockTitle: string;
  blockType?: string;
}

export const getBlockContext = (
  blockId: string,
  contentSpec: WebDashboardContentSpec
): BlockContext | null => {
  const block = contentSpec.blocks.find(b => b.block_id === blockId);

  if (!block) {
    return null;
  }

  return {
    blockId,
    blockTitle: resolveBlockTitle(block),
    blockType: block.type,
  };
};

function hasUserFacingChatContent(message: Message): boolean {
  if (message.role === "user") {
    return true;
  }

  const content = message.content;
  return Boolean(
    content.text ||
      content.questions ||
      content.save_memory_confirmation ||
      content.created_custom_theme ||
      content.dashboard_updated ||
      content.web_dashboard_json?.content_config ||
      content.web_dashboard_plan ||
      content.screenshot ||
      content.pause_terminal
  );
}

/**
 * Durable payloads that actually render in MessageComponent. Excludes `web_dashboard_plan`:
 * builder threads attach that to many tool messages; the plan is not shown as bubble body
 * (only text/reasoning are), so including it produced empty assistant rows above the loader.
 */
function hasRenderableDurablePayload(message: Message): boolean {
  const c = message.content;
  return Boolean(
    c.questions ||
      c.save_memory_confirmation ||
      c.created_custom_theme ||
      c.dashboard_updated ||
      c.web_dashboard_json?.content_config ||
      c.screenshot ||
      c.pause_terminal
  );
}

function hasStandaloneChatContent(message: Message, isBuilderMode: boolean): boolean {
  if (message.role === "user") {
    return true;
  }

  if (message.role === "event") {
    return false;
  }

  const content = message.content;
  const visibleCopy = Boolean(getMessageText(message));

  // Dashboard build mode: assistant narration and tool turns live in ThinkingLoader + step list.
  // Only surface user-visible durable payloads (saved config, questions, …) or a final text-only reply.
  if (isBuilderMode) {
    if (hasRenderableDurablePayload(message)) {
      return true;
    }
    if (!content.tool_calls?.length && visibleCopy) {
      return true;
    }
    return false;
  }

  // Tool-call turns: never surface the model's pre-tool narration as a bubble (e.g. "Let me get
  // the block spec first." on a get_block_spec/run_sqlite_query turn). Progress is shown by the
  // ThinkingLoader, and the real reply always arrives as a separate no-tool_calls message. Only
  // render when the turn carries a durable payload (dashboard mod card, questions, screenshot, …).
  if (content.tool_calls?.length) {
    return hasRenderableDurablePayload(message);
  }

  return Boolean(
    visibleCopy ||
      content.questions ||
      content.save_memory_confirmation ||
      content.created_custom_theme ||
      content.dashboard_updated ||
      content.web_dashboard_json?.content_config ||
      content.screenshot ||
      content.pause_terminal
  );
}

interface PendingMessage {
  id: string;
  role: "user" | "agent";
  content: string;
  autoSubmit?: boolean;
  /**
   * Client-only message with no server counterpart (e.g. an instant "Explain"
   * answer rendered from a pre-computed block explanation). Such messages have
   * no server echo to supersede them, so the absorb/clear logic must keep them
   * until the user actually submits a follow-up.
   */
  instant?: boolean;
  /** If set, this content is sent to the backend instead of `content` (which is only shown in the UI). */
  backendContent?: string;
  meta?: {
    auto_fix?: {
      is_auto_fix: true;
      attempt_index?: number;
      max_attempts?: number;
      error_message?: string;
      source?: "fix_button" | "auto_fix_retry";
    };
  };
}

interface UndoStackEntry {
  id: string;
  contentConfig: WebDashboardContentConfig;
  spreadsheetVersionId?: number;
}

interface ChatSidebarProps {
  webDashboardId: string;
  spreadsheetId: string;
  sourceType?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onDashboardUpdate?: (config: Partial<WebDashboardContentConfig>, messageId?: string) => void;
  onBlockScreenshotCaptureRequest?: (request: { requestId: string; blockIds: string[] }) => void;
  targetDocument?: Document;
  targetWindow?: DashboardWindow;
  blockContexts?: BlockContext[];
  onRemoveBlockContext?: (blockId: string) => void;
  onThinkingStateChange?: (isThinking: boolean) => void;
  /** Optional callback to inform the parent which blocks are currently being updated */
  onRefreshingBlockIdsChange?: (blockIds: Set<string>) => void;
  initialSummary?: string;
  pendingMessages: PendingMessage[];
  addPendingMessage: (message: PendingMessage) => void;
  clearPendingMessages: () => void;
  /** The mode of the chat sidebar */
  mode: ChatMode;
  /** When true, the dashboard is archived: the chat input is replaced with a read-only notice. */
  isArchived?: boolean;
  /** Optional existing thread to show directly (used by dashboard create/builder mode). */
  initialThreadId?: string | null;
  onOpenThemes?: () => void;
  onRevertToMessage?: (
    agentMessageId: string,
    userMessageId: string,
    threadId: string
  ) => Promise<void>;
  messageUndoEntryIds?: Map<string, string>;
  undoStack?: UndoStackEntry[];
  /** Current sidebar width in pixels */
  width?: number;
  /** Callback when sidebar width changes during resize */
  onWidthChange?: (width: number) => void;
  /** Whether the sidebar is currently being resized */
  isResizing?: boolean;
  /** Callback when resize state changes */
  onResizingChange?: (isResizing: boolean) => void;
  /** Super admin history mode props (only used when mode === "superAdminHistory") */
  superAdminHistory?: SuperAdminHistoryProps;
  /** Called when auto-fix reaches attempt limit */
  onAutoFixExhausted?: (summary: string) => void;
  /** Full list of blocks in the dashboard (for @ autocomplete and DOM picker) */
  availableBlocks?: WebDashboardContentSpecBlocksInner[];
  /** Callback to add a block to the chat context */
  onAddBlockContext?: (ctx: BlockContext) => void;
  /** Callback when the set of block mentions in the editor changes (e.g. chip removed) */
  onBlockContextsChange?: (
    contexts: Array<{ blockId: string; blockTitle: string; blockType: string }>
  ) => void;

  /** Which OAuth provider is required to proceed (if any) */
  oauthRequiredProvider?: OAuthProvider | null;
  /** Trigger OAuth flow */
  onConnectOAuth?: () => void;
  /** Dismiss the OAuth-required card */
  onDismissOAuthRequired?: () => void;
  /** Called when the backend indicates OAuth is required */
  onNeedsOAuth?: (err: NeedsGoogleOAuthError) => void;
  /** Pre-populate the chat input without submitting. Clears after the user edits or submits. */
  prefillInput?: string;
  /**
   * Appended to the message actually sent to the agent when the user submits a prefill they built
   * on — e.g. "(theme id: …)" for the selector's Edit action. Never shown in the composer.
   */
  prefillHiddenContext?: string;
  /** Called after prefillInput has been applied so the parent can reset it. */
  onPrefillConsumed?: () => void;
  /**
   * Submit this message programmatically as soon as the chat is idle (e.g. the theme-request
   * modal handing off to the agent). Kept pending until consumed, so it fires once loading
   * settles.
   */
  autoSubmitMessage?: string;
  /** Called once autoSubmitMessage has been submitted so the parent can reset it. */
  onAutoSubmitConsumed?: () => void;
  /** Show the in-chat custom-theme request form (opened from the theme selector). */
  isThemeRequestOpen?: boolean;
  /** Changes to remount the card as a blank form (the previous request was already submitted). */
  themeRequestInstanceId?: number;
  /** Changes on every "+ Custom" click: scroll the card into view and focus it. */
  themeRequestFocusNonce?: number;
  /** Theme request stored: the parent hands it off to the agent (when the gate allows). */
  onThemeRequestSubmitted?: (submitted: {
    userRequestId: string;
    description: string;
    summary: string;
    tags: string[];
    visibleRequest: string;
  }) => void;
  /** The user dismissed the theme request card. */
  onThemeRequestDismissed?: () => void;
  /** The agent asked for the theme form (show_theme_request_form). */
  onThemeFormRequested?: () => void;
  /** Theme result card actions (create_custom_theme replies): live preview / persist / revert. */
  onThemeCardPreview?: (themePointer: string) => void;
  onThemeCardApply?: (themePointer: string) => void;
  onThemeCardCancelPreview?: () => void;
}

const getBlockIdSetKey = (blockIds: Set<string>): string => JSON.stringify([...blockIds].sort());

export interface ChatSidebarHandle {
  insertBlockChip: (blockId: string, blockTitle: string, blockType: string) => void;
  getBlockMentions: () => Array<{ blockId: string; blockTitle: string; blockType: string }>;
}

const BuilderAssistantIntro: React.FC = () => (
  <div className="bg-white py-2 text-sm text-gray-900">
    <p className="mb-2 font-semibold">Let&apos;s build your dashboard!</p>
    <p>
      I&apos;m going to analyze your entire spreadsheet and then create a live, shareable dashboard
      with charts, filters, KPIs, and more.
    </p>
  </div>
);

export const WebDashboardChatSidebar = forwardRef<ChatSidebarHandle, ChatSidebarProps>(
  (
    {
      webDashboardId,
      spreadsheetId,
      sourceType,
      isOpen,
      onClose,
      onDashboardUpdate,
      onBlockScreenshotCaptureRequest,
      targetDocument = document,
      targetWindow,
      blockContexts = [],
      onRemoveBlockContext,
      onThinkingStateChange,
      onRefreshingBlockIdsChange,
      initialSummary,
      pendingMessages,
      addPendingMessage,
      clearPendingMessages,
      mode,
      initialThreadId,
      onOpenThemes,
      onRevertToMessage,
      messageUndoEntryIds,
      undoStack,
      width = 360,
      onWidthChange,
      isResizing = false,
      onResizingChange,
      superAdminHistory,
      onAutoFixExhausted,
      availableBlocks,
      onAddBlockContext,
      onBlockContextsChange,
      oauthRequiredProvider = null,
      onConnectOAuth,
      onDismissOAuthRequired,
      onNeedsOAuth,
      prefillInput,
      prefillHiddenContext,
      onPrefillConsumed,
      autoSubmitMessage,
      onAutoSubmitConsumed,
      isThemeRequestOpen = false,
      themeRequestInstanceId = 0,
      themeRequestFocusNonce = 0,
      onThemeRequestSubmitted,
      onThemeRequestDismissed,
      onThemeFormRequested,
      onThemeCardPreview,
      onThemeCardApply,
      onThemeCardCancelPreview,
      isArchived = false,
    },
    forwardedRef
  ) => {
    // Mode-derived values
    // Destructure super admin history props (only defined when mode === "superAdminHistory")
    const { onLoadVersion, isLoadingVersion = false } = superAdminHistory ?? {};
    const isSuperAdminHistoryMode = mode === "superAdminHistory";
    const isBuilderMode = mode === "builder";
    const isViewerMode = mode === "viewer";
    const isVideoPromoDismissed = useAiDashboardsVideoPromoDismissed();

    const [isHelpVideoModalOpen, setIsHelpVideoModalOpen] = useState(false);

    // Header title based on mode
    const headerTitle = isSuperAdminHistoryMode
      ? "Editor Thread History"
      : "Dashboard AI Assistant";

    const dismissHelpVideoCard = useCallback(() => {
      dismissAiDashboardsVideoPromo();
    }, []);

    const openHelpVideoModal = useCallback(() => {
      setIsHelpVideoModalOpen(true);
    }, []);

    const closeHelpVideoModal = useCallback(() => {
      setIsHelpVideoModalOpen(false);
    }, []);

    // Use the unified chat messages hook
    const {
      messages,
      isLoading,
      isLoadingExistingThreads,
      threadId,
      runStatus,
      isThinking,
      setIsThinking,
      initialVersionS3Path,
      dashboardCreatedDt,
      submitMessage,
      processedMessageIds,
      markMessageProcessed,
    } = useChatMessages({
      mode,
      webDashboardId,
      spreadsheetId,
      sourceType,
      enabled: isOpen,
      initialThreadId,
    });
    const isBuilderInputLocked = isBuilderMode && runStatus !== "completed";

    const { value: isDashboardAutoFixEnabled } = useFeatureGate(
      FeatureFlag.EnableWebDashboardAutofix
    );

    const [isMemoryDrawerOpen, setIsMemoryDrawerOpen] = useState(false);
    const openMemoryDrawer = useCallback(() => setIsMemoryDrawerOpen(true), []);

    // Confirm/reject a pending save_memory proposal. Handled synchronously by the
    // server (persist/discard + a deterministic reply message) — no agent run.
    const handleSaveMemoryDecision = useCallback(
      async (proposalId: string, decision: "confirm" | "reject") => {
        await submitMessage(
          decision === "confirm" ? "Save to memory" : "Don't save",
          undefined,
          undefined,
          { save_memory_decision: { proposal_id: proposalId, decision } }
        );
      },
      [submitMessage]
    );

    // Only the newest message's confirm card is actionable: once a decision (or any
    // newer message) lands, older cards render as inactive.
    const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

    const { isAutoFixInFlight, onDashboardUpdated, resetForNewUserMessage } = useDashboardAutoFix({
      enabled: isDashboardAutoFixEnabled && !isBuilderMode,
      mode,
      targetWindow,
      threadId,
      webDashboardId,
      runStatus,
      submitMessage,
      addPendingMessage,
      setIsThinking,
      onAutoFixExhausted,
    });

    // Local UI state
    const [inputValue, setInputValue] = useState("");
    const [isMounted, setIsMounted] = useState(false);

    // The prefill the composer was seeded with, and the context to append when the user sends
    // something built on it. Dropped if they replace the text with their own message.
    const prefillContextRef = useRef<{ visible: string; hidden: string } | null>(null);

    useEffect(() => {
      if (!prefillInput) {
        return;
      }
      prefillContextRef.current = prefillHiddenContext
        ? { visible: prefillInput.trim(), hidden: prefillHiddenContext }
        : null;
      setInputValue(prefillInput);
      chatInputRef.current?.setText(prefillInput);
      onPrefillConsumed?.();
    }, [prefillInput]); // eslint-disable-line react-hooks/exhaustive-deps
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

    // Block picker (DOM overlay) and @ mention — same add-context path
    const handleAddBlockContext = useCallback(
      (blockId: string, blockTitle: string, blockType: string) => {
        onAddBlockContext?.({ blockId, blockTitle, blockType });
      },
      [onAddBlockContext]
    );

    const chatInputRef = useRef<ChatInputHandle>(null);

    useImperativeHandle(forwardedRef, () => ({
      insertBlockChip(blockId: string, blockTitle: string, blockType: string) {
        chatInputRef.current?.insertBlockChip(blockId, blockTitle, blockType);
      },
      getBlockMentions() {
        return chatInputRef.current?.getBlockMentions() ?? [];
      },
    }));

    // Wire the block picker to replace the active @mention with a chip
    const handleBlockPicked = useCallback(
      (blockId: string, blockTitle: string, blockType: string) => {
        chatInputRef.current?.replaceMentionWithChip(blockId, blockTitle, blockType);
      },
      []
    );

    const { isPickerActive, activatePicker, deactivatePicker } = useBlockPicker({
      targetDocument,
      blocks: availableBlocks ?? [],
      onBlockPicked: handleBlockPicked,
    });

    // Maps block_id -> type so message block badges render the per-type icon (matching the
    // @ autocomplete and composer chip) instead of a generic dashboard icon.
    const blockTypeById = useMemo(() => {
      const map: Record<string, string> = {};
      for (const block of availableBlocks ?? []) {
        map[block.block_id] = block.type;
      }
      return map;
    }, [availableBlocks]);

    // Track which messages have been reverted in the current session (before server sync)
    const [sessionRevertedMessageIds, setSessionRevertedMessageIds] = useState<Set<string>>(
      new Set()
    );

    // State for staged reveal of pending messages
    const [alreadyRevealedPendingMessageIds, setAlreadyRevealedPendingMessageIds] = useState<
      Set<string>
    >(new Set());
    const [showSyntheticThinking, setShowSyntheticThinking] = useState(false);
    const [lastMessageIdBeforePending, setLastMessageIdBeforePending] = useState<string | null>(
      null
    );

    const chatMessagesRef = useRef<HTMLDivElement>(null);
    const processedScreenshotRequestIdsRef = useRef<Set<string>>(new Set());
    const lastEmittedRefreshingBlockIdsKeyRef = useRef<string | null>(null);

    const emitRefreshingBlockIdsChange = useCallback(
      (blockIds: Set<string>) => {
        const nextKey = getBlockIdSetKey(blockIds);
        if (lastEmittedRefreshingBlockIdsKeyRef.current === nextKey) {
          return;
        }
        lastEmittedRefreshingBlockIdsKeyRef.current = nextKey;
        onRefreshingBlockIdsChange?.(blockIds);
      },
      [onRefreshingBlockIdsChange]
    );

    const triggerScreenshotCaptureFromToolCalls = useCallback(
      (message: Message): boolean => {
        let didTriggerScreenshot = false;
        try {
          const toolCalls = message.content?.tool_calls ?? [];
          for (const tc of toolCalls) {
            const fn = (tc as any)?.function;
            const name = fn?.name as string | undefined;
            if (name !== "start_block_screenshot_capture") {
              continue;
            }

            const argsRaw = fn?.arguments as string | undefined;
            if (!argsRaw) {
              continue;
            }

            const args = JSON.parse(argsRaw);
            const requestId = args?.request_id ?? args?.input?.request_id;
            const blockIdsRaw = args?.block_ids ?? args?.input?.block_ids;
            const blockIds = Array.isArray(blockIdsRaw) ? blockIdsRaw.filter(Boolean) : [];
            if (!requestId || blockIds.length === 0) {
              continue;
            }

            if (processedScreenshotRequestIdsRef.current.has(requestId)) {
              continue;
            }
            processedScreenshotRequestIdsRef.current.add(requestId);
            onBlockScreenshotCaptureRequest?.({ requestId, blockIds });
            didTriggerScreenshot = true;
          }
        } catch {
          // Ignore best-effort parsing errors.
        }

        return didTriggerScreenshot;
      },
      [onBlockScreenshotCaptureRequest]
    );

    const applyDashboardConfigFromMessage = useCallback(
      (message: Message): boolean => {
        const contentConfig = message.content?.web_dashboard_json?.content_config;
        if (!contentConfig) {
          return false;
        }

        onDashboardUpdated();
        onDashboardUpdate?.(contentConfig, message.id);
        return true;
      },
      [onDashboardUpdate, onDashboardUpdated]
    );

    // Process chat message side effects (for editor mode).
    // Note: Initial messages are pre-added to processedMessageIds by the hook,
    // so they won't trigger side effects here.
    const processChatMessageSideEffects = useCallback(
      (message: Message) => {
        // Avoid re-processing old messages (including old screenshot tool calls) which can
        // retrigger captures for expired request IDs.
        if (processedMessageIds.has(message.id)) {
          return;
        }

        const didTriggerScreenshot = triggerScreenshotCaptureFromToolCalls(message);
        const didApplyDashboardConfig = applyDashboardConfigFromMessage(message);

        // Mark message as processed if either side effect ran. This also ensures
        // screenshot tool-call-only messages don't retrigger.
        if (didTriggerScreenshot || didApplyDashboardConfig) {
          markMessageProcessed(message.id);
        }
      },
      [
        processedMessageIds,
        triggerScreenshotCaptureFromToolCalls,
        applyDashboardConfigFromMessage,
        markMessageProcessed,
      ]
    );

    // Process messages for dashboard side effects
    useEffect(() => {
      if (isSuperAdminHistoryMode || isBuilderMode) {
        return;
      }
      messages.forEach(msg => processChatMessageSideEffects(msg));
    }, [messages, isBuilderMode, isSuperAdminHistoryMode, processChatMessageSideEffects]);

    // Compute persisted reverted message IDs from EVENT messages in the thread
    const persistedRevertedMessageIds = useMemo(
      () => computeRevertedMessageIds(messages),
      [messages]
    );

    // Combined reverted IDs: persisted (from server) + session (local, before refresh)
    const revertedMessageIds = useMemo(
      () => new Set([...persistedRevertedMessageIds, ...sessionRevertedMessageIds]),
      [persistedRevertedMessageIds, sessionRevertedMessageIds]
    );

    // Filter out EVENT messages for display, keeping track of reverted status
    type DisplayItem =
      | { type: "message"; message: Message }
      | { type: "revertedGroup"; messages: Message[] };

    const displayItems = useMemo(() => {
      const items: DisplayItem[] = [];
      let currentRevertedGroup: Message[] = [];

      // Filter out EVENT messages and (in builder mode) assistant tool narrations. Progress is
      // shown in ThinkingLoader + completed steps, not as chat bubbles.
      const nonEventMessages = messages.filter(m => hasStandaloneChatContent(m, isBuilderMode));

      for (const msg of nonEventMessages) {
        const isReverted = revertedMessageIds.has(msg.id);

        if (isReverted) {
          currentRevertedGroup.push(msg);
        } else {
          if (currentRevertedGroup.length > 0) {
            items.push({ type: "revertedGroup", messages: currentRevertedGroup });
            currentRevertedGroup = [];
          }
          items.push({ type: "message", message: msg });
        }
      }

      if (currentRevertedGroup.length > 0) {
        items.push({ type: "revertedGroup", messages: currentRevertedGroup });
      }

      return items;
    }, [messages, revertedMessageIds, isBuilderMode]);

    // Filter messages for history mode display
    const filteredHistoryMessages = useMemo(() => {
      if (!isSuperAdminHistoryMode) {
        return [];
      }
      return messages.filter(hasUserFacingChatContent);
    }, [isSuperAdminHistoryMode, messages]);
    const shouldShowBuilderThinkingFallback =
      isBuilderMode && !initialSummary && messages.length === 0 && pendingMessages.length === 0;
    const shouldShowThinkingLoader =
      isThinking || showSyntheticThinking || shouldShowBuilderThinkingFallback;

    // Thread pause/stop mutation
    const postAgentThreadPauseMutation = usePostAgentThreadPauseMutation();
    const [isPausing, setIsPausing] = useState(false);

    const stopThread = useCallback(async () => {
      if (!threadId) {
        return;
      }
      if (isPausing) {
        return;
      }
      try {
        setIsPausing(true);
        await postAgentThreadPauseMutation.mutateAsync(threadId);
      } catch (e) {
        console.error("[vibedash] Failed to pause agent thread", e);
        setIsPausing(false);
      }
    }, [threadId, postAgentThreadPauseMutation, isPausing]);

    // Once we’re no longer in an in-flight state, re-enable the STOP button.
    useEffect(() => {
      if (isPausing && !isThinking) {
        setIsPausing(false);
      }
    }, [isPausing, isThinking]);

    // Notify parent when thinking state changes
    useEffect(() => {
      onThinkingStateChange?.(isThinking);
    }, [isThinking, onThinkingStateChange]);

    const extractBlockIdsFromUpdateWebDashboardToolCalls = useCallback(
      (toolCalls: AIToolCall[] | undefined): Set<string> => {
        const ids = new Set<string>();
        if (!toolCalls || toolCalls.length === 0) {
          return ids;
        }
        for (const tc of toolCalls) {
          const name = tc?.function?.name;
          if (name !== "update_web_dashboard") {
            continue;
          }
          const argsStr = tc.function.arguments;
          if (!argsStr) {
            continue;
          }
          try {
            const args = JSON.parse(argsStr);
            // Most tool calls wrap the payload under { input: {...} }
            const payload = args?.input ?? args;
            const updates = payload?.block_updates;
            if (Array.isArray(updates)) {
              for (const u of updates) {
                const blockId = u?.block_id;
                if (typeof blockId === "string") {
                  ids.add(blockId);
                }
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(
              "[vibedash] Failed to parse update_web_dashboard tool call arguments",
              error,
              argsStr
            );
          }
        }
        return ids;
      },
      []
    );

    // Notify parent which blocks should glow while the agent is in-flight. If the user selected
    // blocks in the chat context, prefer those. Otherwise, try to infer from tool calls.
    useEffect(() => {
      if (!onRefreshingBlockIdsChange) {
        return;
      }

      if (!isThinking) {
        emitRefreshingBlockIdsChange(new Set());
        return;
      }

      if (blockContexts.length > 0) {
        emitRefreshingBlockIdsChange(new Set(blockContexts.map(ctx => ctx.blockId)));
        return;
      }

      const lastMessage = messages?.[messages.length - 1];
      const inferred = extractBlockIdsFromUpdateWebDashboardToolCalls(
        lastMessage?.content?.tool_calls
      );
      emitRefreshingBlockIdsChange(inferred);
    }, [
      isThinking,
      blockContexts,
      messages,
      onRefreshingBlockIdsChange,
      extractBlockIdsFromUpdateWebDashboardToolCalls,
      emitRefreshingBlockIdsChange,
    ]);

    // Mark component as mounted after initial render to enable transitions
    useEffect(() => {
      setIsMounted(true);
    }, []);

    // Bumped when the docked theme request form opens or closes, so the scroll effect reruns.
    const [themeRequestRevision, setThemeRequestRevision] = useState(0);
    const { value: isCustomThemeAgentGateOn } = useFeatureGate(
      FeatureFlag.EnableWebDashboardCustomThemeCreation
    );
    // Persisted records of submitted theme requests, rendered in the transcript.
    const [themeReceipts, setThemeReceipts] = useState<ThemeRequestReceipt[]>(() =>
      readThemeRequestReceipts(webDashboardId)
    );
    const handleThemeRequestCardSubmitted = useCallback(
      (submitted: {
        userRequestId: string;
        description: string;
        summary: string;
        tags: string[];
        visibleRequest: string;
      }) => {
        // Gated on, the request becomes a normal chat turn (the user's words, then the agent's
        // reply and swatch card) — a receipt card as well would repeat it, out of order.
        if (!isCustomThemeAgentGateOn) {
          setThemeReceipts(
            appendThemeRequestReceipt(webDashboardId, {
              userRequestId: submitted.userRequestId,
              tags: submitted.tags,
              summary: submitted.summary,
              submittedAt: new Date().toISOString(),
              afterMessageId: messages[messages.length - 1]?.id ?? null,
            })
          );
        }
        setThemeRequestRevision(n => n + 1);
        onThemeRequestSubmitted?.(submitted);
        // The form is done; the transcript receipt is the lasting record.
        onThemeRequestDismissed?.();
      },
      [
        webDashboardId,
        messages,
        isCustomThemeAgentGateOn,
        onThemeRequestSubmitted,
        onThemeRequestDismissed,
      ]
    );

    // The agent can ask for the form instead of interviewing the user in text
    // (show_theme_request_form). The payload can land on any message of the turn, so take the
    // newest one carrying it and fire once per message id.
    //
    // "Fresh reply" is decided against a snapshot of the thread's loaded history — NOT
    // submissionStartedRef, which the pending-queue drain clears as soon as the server echoes the
    // user message, i.e. a poll before the agent's reply arrives. See themeHistorySnapshot.ts.
    const themeFormRequestedForRef = useRef<string | null>(null);
    const themeHistorySnapshotRef = useRef<ThemeHistorySnapshot | null>(null);
    useEffect(() => {
      const taken = nextThemeHistorySnapshot({
        snapshot: themeHistorySnapshotRef.current,
        threadId: threadId ?? null,
        messageIds: messages.map(m => m.id),
        isLoading,
        isLoadingExistingThreads,
      });
      if (taken) {
        themeHistorySnapshotRef.current = taken;
        return;
      }
      const snapshot = themeHistorySnapshotRef.current;
      const withForm = [...messages]
        .reverse()
        .find(
          m =>
            m.content?.theme_request_form && isFreshThemeMessage(snapshot, m.id, threadId ?? null)
        );
      if (!withForm || themeFormRequestedForRef.current === withForm.id) {
        return;
      }
      themeFormRequestedForRef.current = withForm.id;
      onThemeFormRequested?.();
    }, [messages, threadId, isLoading, isLoadingExistingThreads, onThemeFormRequested]);

    // A theme reply may live-preview itself only if it arrived after the snapshot above — same
    // reasoning as the form: reopening chat history must never flip the dashboard's theme.
    const isFreshThemeReply = useCallback(
      (messageId: string) =>
        isFreshThemeMessage(themeHistorySnapshotRef.current, messageId, threadId ?? null),
      [threadId]
    );

    // Receipts render after the message that was last when they were submitted, so scrolling back
    // finds the request where it happened.
    const themeReceiptsByAnchor = useMemo(() => {
      const byAnchor = new Map<string, ThemeRequestReceipt[]>();
      themeReceipts.forEach(receipt => {
        if (!receipt.afterMessageId) {
          return;
        }
        byAnchor.set(receipt.afterMessageId, [
          ...(byAnchor.get(receipt.afterMessageId) ?? []),
          receipt,
        ]);
      });
      return byAnchor;
    }, [themeReceipts]);

    // Submitted on an empty thread, or anchored to a message the transcript no longer shows.
    const unanchoredThemeReceipts = useMemo(() => {
      const shownIds = new Set(messages.map(m => m.id));
      return themeReceipts.filter(r => !r.afterMessageId || !shownIds.has(r.afterMessageId));
    }, [themeReceipts, messages]);

    const removeThemeReceipt = useCallback(
      (userRequestId: string) => {
        setThemeReceipts(removeThemeRequestReceipt(webDashboardId, userRequestId));
      },
      [webDashboardId]
    );

    // Scroll to bottom when messages change, or when the theme request card opens/collapses —
    // it is a tall element outside the message list, so nothing else reports its height change.
    useEffect(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }, [
      messages,
      isThinking,
      pendingMessages,
      alreadyRevealedPendingMessageIds,
      showSyntheticThinking,
      isThemeRequestOpen,
      themeRequestRevision,
      // A repeat "+ Custom" click leaves isThemeRequestOpen true, so the nonce is what brings an
      // already-open card back into view.
      themeRequestFocusNonce,
    ]);

    // Keep lastMessageIdBeforePending in sync with the last message ID while idle
    useEffect(() => {
      if (pendingMessages.length === 0) {
        setLastMessageIdBeforePending(messages[messages.length - 1]?.id ?? null);
      }
    }, [pendingMessages, messages]);

    // Clear pending messages when server returns messages beyond the snapshot
    useEffect(() => {
      if (pendingMessages.length === 0) {
        return;
      }

      // Instant (client-only) messages have no server echo to supersede them.
      // Until the user submits a follow-up (which flips submissionStartedRef),
      // the messages that show up here come from the initial thread load — not
      // from anything we sent — so absorbing/clearing them would wipe the
      // instant answer right after the sidebar opens.
      const hasInstant = pendingMessages.some(pm => pm.instant);
      if (hasInstant && !submissionStartedRef.current) {
        return;
      }

      // Don't clear if there are unprocessed auto-submit messages still waiting to be sent
      const hasUnprocessedAutoSubmit = pendingMessages.some(
        pm => pm.autoSubmit && !autoSubmittedIdsRef.current.has(pm.id)
      );

      if (lastMessageIdBeforePending === null) {
        if (messages.length > 0 && !hasUnprocessedAutoSubmit) {
          clearPendingMessages();
        }
        return;
      }

      const snapshotIndex = messages.findIndex(m => m.id === lastMessageIdBeforePending);
      const hasNewMessagesAfterSnapshot =
        (snapshotIndex === -1 && messages.length > 0) ||
        (snapshotIndex >= 0 && snapshotIndex < messages.length - 1);

      if (hasNewMessagesAfterSnapshot) {
        clearPendingMessages();
      }
    }, [messages, lastMessageIdBeforePending, pendingMessages, clearPendingMessages]);

    // True once the user has submitted while instant pending messages are
    // showing. Distinguishes server messages arriving from the initial thread
    // load (keep the instant answer) from those arriving in response to a
    // submission (clear it). Reset when the pending queue drains.
    const submissionStartedRef = useRef(false);

    // Reset revealed IDs when pendingMessages becomes empty
    useEffect(() => {
      if (pendingMessages.length === 0) {
        setAlreadyRevealedPendingMessageIds(new Set());
        setShowSyntheticThinking(false);
        submissionStartedRef.current = false;
      }
    }, [pendingMessages]);

    // Progressive reveal effect for pending messages
    useEffect(() => {
      const nextMessage = pendingMessages.find(pm => !alreadyRevealedPendingMessageIds.has(pm.id));

      if (isThinking && nextMessage) {
        setAlreadyRevealedPendingMessageIds(new Set(pendingMessages.map(pm => pm.id)));
        setShowSyntheticThinking(false);
        return;
      }

      if (!nextMessage) {
        setShowSyntheticThinking(false);
        return;
      }

      if (nextMessage.role === "user") {
        const timer = setTimeout(() => {
          setAlreadyRevealedPendingMessageIds(prev => new Set([...prev, nextMessage.id]));
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setShowSyntheticThinking(true);
        const timer = setTimeout(() => {
          setShowSyntheticThinking(false);
          setAlreadyRevealedPendingMessageIds(prev => new Set([...prev, nextMessage.id]));
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [pendingMessages, alreadyRevealedPendingMessageIds, isThinking]);

    // Auto-submit pending messages flagged with autoSubmit
    const autoSubmittedIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
      if (!isOpen || isThinking || isLoading || isLoadingExistingThreads) {
        return;
      }

      const autoSubmitMsg = pendingMessages.find(
        pm => pm.autoSubmit && pm.role === "user" && !autoSubmittedIdsRef.current.has(pm.id)
      );
      if (!autoSubmitMsg) {
        return;
      }

      autoSubmittedIdsRef.current.add(autoSubmitMsg.id);

      const doAutoSubmit = async () => {
        submissionStartedRef.current = true;
        setIsThinking(true);
        try {
          const priorMessages = pendingMessages
            .filter(pm => pm.id !== autoSubmitMsg.id)
            .map(pm => ({ role: pm.role, content: pm.content }));

          const messageToSend = autoSubmitMsg.backendContent ?? autoSubmitMsg.content;

          await submitMessage(
            messageToSend,
            priorMessages.length > 0 ? priorMessages : undefined,
            undefined,
            autoSubmitMsg.meta
          );
        } catch (error) {
          console.error("Auto-submit error:", error);
          if (error instanceof NeedsGoogleOAuthError) {
            onNeedsOAuth?.(error);
          } else if (error instanceof DashboardArchivedError) {
            antMessage.warning(error.message);
          }
          clearPendingMessages();
          setIsThinking(false);
        }
      };

      doAutoSubmit();
    }, [
      pendingMessages,
      isOpen,
      isThinking,
      isLoading,
      isLoadingExistingThreads,
      submitMessage,
      clearPendingMessages,
      setIsThinking,
    ]);

    // Handle chat input height changes
    const checkIfScrolledToBottom = useCallback(() => {
      const el = chatMessagesRef.current;
      if (!el) {
        return false;
      }
      return el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    }, []);

    const handleInputHeightChanged = useCallback((wasAtBottom: boolean) => {
      if (wasAtBottom && chatMessagesRef.current) {
        chatMessagesRef.current.scrollTo({
          top: chatMessagesRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, []);

    // Update body class when sidebar opens/closes
    useEffect(() => {
      if (isOpen) {
        targetDocument.body.classList.add("chat-open");
      } else {
        targetDocument.body.classList.remove("chat-open");
      }
      return () => {
        targetDocument.body.classList.remove("chat-open");
      };
    }, [isOpen, targetDocument]);

    // Derive revealed pending messages
    const revealedPendingMessages = useMemo(() => {
      return pendingMessages.filter(pm => alreadyRevealedPendingMessageIds.has(pm.id));
    }, [pendingMessages, alreadyRevealedPendingMessageIds]);

    // Check if pending messages have been absorbed
    const displayedPendingMessages = useMemo(() => {
      if (pendingMessages.length === 0) {
        return [];
      }

      // Instant (client-only) messages aren't echoed by the server, so the
      // initial thread load must not be treated as absorbing them. Keep showing
      // them until a follow-up submission supersedes them. (Mirrors the guard in
      // the clear effect above.)
      const hasInstant = pendingMessages.some(pm => pm.instant);
      if (hasInstant && !submissionStartedRef.current) {
        return isThinking ? pendingMessages : revealedPendingMessages;
      }

      const currentLastMessageId = messages[messages.length - 1]?.id ?? null;
      const messagesAbsorbed =
        lastMessageIdBeforePending === null
          ? messages.length > 0
          : currentLastMessageId !== lastMessageIdBeforePending;

      if (messagesAbsorbed) {
        return [];
      }

      return isThinking ? pendingMessages : revealedPendingMessages;
    }, [
      pendingMessages,
      messages,
      lastMessageIdBeforePending,
      isThinking,
      revealedPendingMessages,
    ]);

    // Handler for reverting a message's changes
    const handleMessageRevert = useCallback(
      async (messageId: string) => {
        if (onRevertToMessage && threadId) {
          const agentMessageIndex = messages.findIndex(m => m.id === messageId);
          if (agentMessageIndex === -1) {
            return;
          }

          let userMessageIndex = agentMessageIndex;
          let userMessageId = messageId;
          for (let i = agentMessageIndex - 1; i >= 0; i--) {
            if (messages[i].role === "user") {
              userMessageIndex = i;
              userMessageId = messages[i].id;
              break;
            }
          }

          await onRevertToMessage(messageId, userMessageId, threadId);

          const idsToHide = messages.slice(userMessageIndex).map(m => m.id);
          setSessionRevertedMessageIds(prev => new Set([...prev, ...idsToHide]));
        }
      },
      [onRevertToMessage, threadId, messages]
    );

    const turnRevertUiByDisplayMessageId = useMemo(
      () =>
        computeTurnRevertUiByDisplayMessageId({
          messages,
          isThinking,
          messageUndoEntryIds,
          undoStack,
        }),
      [messages, isThinking, messageUndoEntryIds, undoStack]
    );

    // Only the most recent dashboard action card should start expanded; older ones collapse so
    // the thread stays scannable.
    const lastDashboardActionCardId = useMemo(() => {
      for (let i = displayItems.length - 1; i >= 0; i--) {
        const item = displayItems[i];
        if (item.type !== "message") {
          continue;
        }
        const msg = item.message;
        if (isUserMessage(msg)) {
          continue;
        }
        const meta = turnRevertUiByDisplayMessageId.get(msg.id);
        if (meta?.showDashboardActionCard ?? hasDashboardModification(msg)) {
          return msg.id;
        }
      }
      return null;
    }, [displayItems, turnRevertUiByDisplayMessageId]);

    /** Core send path shared by the input's submit and programmatic auto-submits. */
    const performSubmit = async (message: string, backendMessage?: string) => {
      submissionStartedRef.current = true;
      setIsThinking(true);
      resetForNewUserMessage();

      const currentPendingMessages = [...pendingMessages];

      // Block ref tags are already inline in the message from the Lexical editor
      // (serialized as [block id="..." title="..."] by serializeEditorState)

      // Add user message to pendingMessages for immediate display
      addPendingMessage({
        id: `pending-user-${Date.now()}`,
        role: "user",
        content: message,
        ...(backendMessage ? { backendContent: backendMessage } : {}),
      });

      try {
        const priorMessages =
          currentPendingMessages.length > 0
            ? currentPendingMessages.map(pm => ({
                role: pm.role,
                content: pm.backendContent ?? pm.content,
              }))
            : undefined;

        await submitMessage(backendMessage ?? message, priorMessages);
      } catch (error) {
        console.error("Error processing message:", error);
        if (error instanceof NeedsGoogleOAuthError) {
          onNeedsOAuth?.(error);
          // Don't restore the draft here; the viewer preserves it via sessionStorage and
          // will auto-submit after OAuth succeeds.
        } else if (error instanceof DashboardArchivedError) {
          antMessage.warning(error.message);
          setInputValue(message);
        } else {
          // Preserve the user's draft if the request failed for non-OAuth reasons.
          setInputValue(message);
        }
        clearPendingMessages();
        setIsThinking(false);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      const message = inputValue.trim();
      if (
        !message ||
        isThinking ||
        isAutoFixInFlight ||
        isLoadingExistingThreads ||
        isSuperAdminHistoryMode
      ) {
        return;
      }

      // Snapshot block mentions from the editor before clearing input
      const mentions = chatInputRef.current?.getBlockMentions() ?? [];
      onBlockContextsChange?.(mentions);

      setInputValue("");
      const prefill = prefillContextRef.current;
      const builtOnPrefill = Boolean(prefill && message.startsWith(prefill.visible));
      prefillContextRef.current = null;
      await performSubmit(
        message,
        builtOnPrefill && prefill ? `${message} ${prefill.hidden}` : undefined
      );
    };

    // Programmatic handoff (e.g. the theme-request modal): submit as soon as the chat is idle.
    // The prop stays set until consumed, so this re-fires when loading/thinking settles.
    const performSubmitRef = useRef(performSubmit);
    performSubmitRef.current = performSubmit;
    useEffect(() => {
      if (
        !autoSubmitMessage ||
        isThinking ||
        isAutoFixInFlight ||
        isLoadingExistingThreads ||
        isSuperAdminHistoryMode
      ) {
        return;
      }
      onAutoSubmitConsumed?.();
      void performSubmitRef.current(autoSubmitMessage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmitMessage, isThinking, isAutoFixInFlight, isLoadingExistingThreads]);

    // Auto-fix runs are now regular visible agent thread runs; no separate silent-run completion path.

    const handleResizeStart = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        onResizingChange?.(true);
        // Prevent text selection while dragging
        targetDocument.body.style.userSelect = "none";
        targetDocument.body.style.cursor = "col-resize";

        const viewportWidth = targetDocument.defaultView?.innerWidth ?? window.innerWidth;

        const onMouseMove = (e: MouseEvent) => {
          const newWidth = Math.min(700, Math.max(300, viewportWidth - e.clientX));
          onWidthChange?.(newWidth);
        };
        const onMouseUp = () => {
          onResizingChange?.(false);
          targetDocument.body.style.userSelect = "";
          targetDocument.body.style.cursor = "";
          targetDocument.removeEventListener("mousemove", onMouseMove);
          targetDocument.removeEventListener("mouseup", onMouseUp);
        };
        targetDocument.addEventListener("mousemove", onMouseMove);
        targetDocument.addEventListener("mouseup", onMouseUp);
      },
      [onWidthChange, onResizingChange, targetDocument]
    );

    return (
      <>
        <aside
          id="chatSidebar"
          // Hidden below the 640px breakpoint: the chat entry point isn't offered on mobile, and a
          // fixed right-0 panel (parked at translate-x-full when closed) would otherwise sit just off
          // the right edge and let the page scroll horizontally past the viewport on a phone.
          className={`fixed right-0 top-0 h-screen bg-white border-l border-gray-200 flex flex-col z-300 shadow-none max-sm:hidden ${
            isMounted && !isResizing ? "transition-transform duration-300 ease-in-out" : ""
          } ${isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
          style={{ width }}
        >
          {/* Resize drag handle */}
          <div
            className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/30 active:bg-blue-400/50 z-10"
            onMouseDown={handleResizeStart}
          />
          {/* Header - pinned for history mode, scrollable for chat modes */}
          {isSuperAdminHistoryMode && (
            <div className="flex items-center justify-between px-4 py-3 pr-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="material-icons text-amber-600">history</span>
                <div className="text-lg font-semibold text-gray-900">{headerTitle}</div>
              </div>
              <button
                id="chatCloseBtn"
                className="relative group transition-colors cursor-pointer flex h-6 w-6 items-center justify-center rounded-full bg-[#f5f5f5] text-[#727272] hover:text-[#111215]"
                aria-label="Hide sidebar"
                onClick={() => {
                  onClose();
                }}
              >
                <span className="material-icons !text-[16px]">close</span>
              </button>
            </div>
          )}
          {/* Floating close button for non-history modes (stays visible on scroll) */}
          {!isSuperAdminHistoryMode && (
            <button
              id="chatCloseBtn"
              className="absolute right-3 top-3 z-10 group transition-colors cursor-pointer flex h-6 w-6 items-center justify-center rounded-full bg-[#f5f5f5] text-[#727272] hover:text-[#111215]"
              aria-label="Hide sidebar"
              onClick={() => {
                onClose();
              }}
            >
              <span className="material-icons !text-[16px]">close</span>
            </button>
          )}

          {/* Info banner - history mode only */}
          {isSuperAdminHistoryMode && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
              <span className="material-icons !text-sm align-middle mr-1">info</span>
              View the AI edit history for this dashboard. Click on a message with a version to
              preview that state.
            </div>
          )}

          {/* Content Area */}
          <div className="relative flex-1 min-h-0">
            {/* History mode: Loading state */}
            {isSuperAdminHistoryMode && isLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 flex flex-col items-center gap-2">
                  <div className="animate-spin">
                    <span className="material-icons">refresh</span>
                  </div>
                  <span>Loading thread history...</span>
                </div>
              </div>
            )}

            {/* History mode: No thread found */}
            {isSuperAdminHistoryMode && !isLoading && !threadId && (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 text-center px-4">
                  <span className="material-icons text-4xl mb-2 block text-gray-400">
                    chat_bubble_outline
                  </span>
                  <p>No editor thread found for this dashboard.</p>
                  <p className="text-sm mt-1">
                    Editor threads are created when users make AI edits.
                  </p>
                </div>
              </div>
            )}

            {/* History mode: No messages */}
            {isSuperAdminHistoryMode &&
              !isLoading &&
              threadId &&
              filteredHistoryMessages.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-500 text-center px-4">
                    <span className="material-icons text-4xl mb-2 block text-gray-400">
                      chat_bubble_outline
                    </span>
                    <p>No messages in this thread yet.</p>
                  </div>
                </div>
              )}

            {/* History mode: Messages list */}
            {isSuperAdminHistoryMode &&
              !isLoading &&
              threadId &&
              filteredHistoryMessages.length > 0 && (
                <div className="h-full overflow-y-auto overscroll-contain px-4 py-4 pb-8 flex flex-col gap-3 items-stretch">
                  {/* Initial Version Entry */}
                  {initialVersionS3Path && dashboardCreatedDt && (
                    <div className="relative border-b border-gray-200 pb-3 mb-1">
                      <div className="text-xs text-gray-400 mb-1">
                        {moment(new Date(dashboardCreatedDt)).format("MMM D, YYYY h:mm A")}
                      </div>

                      <div className="rounded-lg px-3 py-2 bg-green-50 border border-green-200">
                        <div className="flex items-center gap-2 text-green-800">
                          <span className="material-icons !text-sm">flag</span>
                          <span className="text-sm font-medium">Initial Version</span>
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                          Original dashboard state before any AI edits
                        </p>
                      </div>

                      <button
                        onClick={() =>
                          onLoadVersion?.(initialVersionS3Path, new Date(dashboardCreatedDt))
                        }
                        disabled={isLoadingVersion}
                        className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoadingVersion ? (
                          <>
                            <span className="material-icons animate-spin !text-sm">refresh</span>
                            <span>Loading version...</span>
                          </>
                        ) : (
                          <>
                            <span className="material-icons !text-sm">visibility</span>
                            <span>View initial version</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* History messages */}
                  {filteredHistoryMessages.map(msg => {
                    const hasVersion = !!msg.content.web_dash_version_s3_path;
                    const timestamp = new Date(msg.created_dt);

                    return (
                      <div key={msg.id} className="relative">
                        <div className="text-xs text-gray-400 mb-1">
                          {moment(timestamp).format("MMM D, YYYY h:mm A")}
                        </div>

                        <MessageComponent message={msg} blockTypeById={blockTypeById} />

                        {hasVersion && msg.role === "agent" && (
                          <button
                            onClick={() =>
                              onLoadVersion?.(msg.content.web_dash_version_s3_path!, timestamp)
                            }
                            disabled={isLoadingVersion}
                            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isLoadingVersion ? (
                              <>
                                <span className="material-icons animate-spin !text-sm">
                                  refresh
                                </span>
                                <span>Loading version...</span>
                              </>
                            ) : (
                              <>
                                <span className="material-icons !text-sm">visibility</span>
                                <span>View this version</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

            {/* Interactive mode: Messages area */}
            {!isSuperAdminHistoryMode && isLoadingExistingThreads && (
              <div className="h-full px-4 py-4 flex flex-col gap-3 animate-pulse">
                <div className="flex justify-end">
                  <div className="h-10 w-3/4 bg-gray-200 rounded-lg" />
                </div>
                <div className="flex justify-start">
                  <div className="h-16 w-5/6 bg-gray-100 rounded-lg" />
                </div>
                <div className="flex justify-end">
                  <div className="h-10 w-2/3 bg-gray-200 rounded-lg" />
                </div>
              </div>
            )}
            {!isSuperAdminHistoryMode && !isLoadingExistingThreads && (
              <div
                id="chatMessages"
                ref={chatMessagesRef}
                className="h-full overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4 flex flex-col gap-2 items-stretch"
                data-sentry-mask
              >
                {/* Scrollable header title */}
                <div className="flex items-center gap-2 pt-1 pb-1 pr-8">
                  <span className="material-icons text-lg text-gray-900">auto_awesome</span>
                  <div className="text-lg font-semibold text-gray-900">{headerTitle}</div>
                </div>

                {isBuilderMode && <BuilderAssistantIntro />}

                {/* Collapsible dashboard summary & welcome section */}
                {initialSummary && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2 shadow-sm">
                    <div className="text-sm font-semibold text-gray-900 mb-2">
                      {isViewerMode
                        ? "Quick overview of this dashboard"
                        : "Your dashboard is live! 🎉✨"}
                    </div>

                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-2 py-1.5 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => setIsSummaryExpanded(prev => !prev)}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="material-icons text-gray-900"
                          style={{ fontSize: 16, lineHeight: "16px" }}
                        >
                          flare
                        </span>
                        <span className="text-sm font-medium text-gray-900">Dashboard Summary</span>
                      </div>
                      <span className="material-icons text-sm text-gray-500">
                        {isSummaryExpanded ? "expand_less" : "expand_more"}
                      </span>
                    </button>

                    {isSummaryExpanded && (
                      <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                        {initialSummary}
                      </div>
                    )}

                    <div className="mt-3 text-sm text-gray-700">
                      {isViewerMode ? (
                        <>
                          <p className="mb-2">
                            Use this dashboard to understand what&apos;s happening in your data.
                            Here are some ideas for what to ask Coefficient AI.
                          </p>
                          <p className="font-semibold mb-1">What would you like to know?</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Summarize this dashboard and give me key takeaways</li>
                            <li>Highlight important trends or anomalies</li>
                            <li>Explain why a metric moved up or down</li>
                            <li>Suggest follow-up questions I should ask about this data</li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <p className="mb-2">
                            This is your starting point. Explore what&apos;s here, dig deeper into
                            the data, and keep refining until it&apos;s exactly right.
                          </p>
                          <p className="font-semibold mb-1">Here&apos;s what you can do next:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Add, edit, or remove charts and images</li>
                            <li>Drag and drop to rearrange the layout</li>
                            <li>Ask Coefficient AI for insights or deeper explanations</li>
                            <li>
                              Customize the look{" "}
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  onClose();
                                  onOpenThemes?.();
                                }}
                                className="text-blue-600 underline hover:text-blue-700 cursor-pointer"
                              >
                                with themes
                              </button>
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {displayItems.map((item, index) => {
                  if (item.type === "revertedGroup") {
                    return (
                      <RevertedMessageGroup
                        key={`reverted-${item.messages[0]?.id || index}`}
                        messages={item.messages}
                      />
                    );
                  }
                  const msg = item.message;
                  const turnRevertUiMeta = turnRevertUiByDisplayMessageId.get(msg.id);
                  const anchoredReceipts = themeReceiptsByAnchor.get(msg.id);
                  return (
                    <React.Fragment key={msg.id}>
                      <MessageComponent
                        message={msg}
                        onRevert={handleMessageRevert}
                        canRevert={turnRevertUiMeta?.canRevert ?? false}
                        showDashboardActionCard={Boolean(turnRevertUiMeta?.showDashboardActionCard)}
                        actionLabelOverride={turnRevertUiMeta?.actionLabelOverride}
                        revertTargetMessageId={turnRevertUiMeta?.revertTargetMessageId}
                        defaultDetailsExpanded={msg.id === lastDashboardActionCardId}
                        blockTypeById={blockTypeById}
                        onSaveMemoryDecision={handleSaveMemoryDecision}
                        onOpenMemoryDrawer={openMemoryDrawer}
                        saveMemoryActionable={msg.id === lastMessageId && !isThinking}
                        onThemeCardPreview={onThemeCardPreview}
                        onThemeCardApply={onThemeCardApply}
                        onThemeCardCancelPreview={onThemeCardCancelPreview}
                        // The card reads this once, at mount — which can happen mid-turn while the
                        // agent works on, so freshness alone decides (no isThinking/last-message
                        // gate that would be false exactly when the card first appears).
                        shouldAutoPreviewTheme={isFreshThemeReply(msg.id)}
                      />
                      {anchoredReceipts?.map(receipt => (
                        <ChatThemeRequestReceipt
                          key={receipt.userRequestId}
                          receipt={receipt}
                          onRemove={() => removeThemeReceipt(receipt.userRequestId)}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}

                {unanchoredThemeReceipts.map(receipt => (
                  <ChatThemeRequestReceipt
                    key={receipt.userRequestId}
                    receipt={receipt}
                    onRemove={() => removeThemeReceipt(receipt.userRequestId)}
                  />
                ))}

                {/* Pending messages */}
                {displayedPendingMessages.map(pm => (
                  <MessageComponent
                    key={pm.id}
                    message={{
                      id: pm.id,
                      content: { text: pm.content },
                      role: pm.role,
                      thread_id: threadId || "",
                      created_dt: new Date().toISOString(),
                    }}
                    blockTypeById={blockTypeById}
                  />
                ))}

                {!initialSummary &&
                  messages.length === 0 &&
                  pendingMessages.length === 0 &&
                  !shouldShowThinkingLoader && (
                    <div
                      id="chatPlaceholder"
                      className="flex flex-col items-center justify-center flex-1 text-center p-8 text-gray-400"
                    >
                      <span className="material-icons !text-5xl mb-4 opacity-50">
                        chat_bubble_outline
                      </span>
                      <div className="text-base font-medium">
                        {isBuilderMode
                          ? "Waiting for builder updates..."
                          : "Ask me to make changes to your dashboard"}
                      </div>
                    </div>
                  )}

                {shouldShowThinkingLoader &&
                  (isBuilderMode ? (
                    <div className="flex w-full flex-col gap-1">
                      <div id="thinkingBox" className="px-3 py-2">
                        <ThinkingLoader messages={messages} fallbackText="Thinking" />
                      </div>
                    </div>
                  ) : (
                    <div id="thinkingBox" className="px-3 py-2">
                      <ThinkingLoader messages={messages} fallbackText="Thinking" />
                    </div>
                  ))}
              </div>
            )}
            {/* Fade gradient at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          </div>

          {/* Theme request form: a task surface, so it docks above the composer instead of
              interleaving with the transcript. */}
          {isThemeRequestOpen && (
            <div className="px-3 pb-2">
              <ChatThemeRequestCard
                key={themeRequestInstanceId}
                dashboardId={webDashboardId}
                focusNonce={themeRequestFocusNonce}
                onSubmitted={handleThemeRequestCardSubmitted}
                onDismiss={() => onThemeRequestDismissed?.()}
              />
            </div>
          )}

          {/* OAuth reconnect prompt (matches Figma 2682:8600) */}
          {!isSuperAdminHistoryMode && oauthRequiredProvider && (
            <div className="px-4 pb-2">
              <div className="bg-white flex flex-col gap-1 items-start py-2.5 rounded-[12px] w-full">
                <p className="font-bold text-[14px] leading-[18px] text-[#111215] w-full">
                  {oauthRequiredProvider === "microsoft"
                    ? "You need to reconnect Microsoft to Coefficient"
                    : "You need to reconnect Google Sheets to Coefficient"}
                </p>
                <button
                  type="button"
                  onClick={() => onConnectOAuth?.()}
                  className="w-full h-[32px] rounded-[4px] bg-[#3053ff] hover:bg-[#2746d9] transition-colors flex items-center justify-center px-[12px] py-[7px]"
                >
                  <span className="font-semibold text-[14px] leading-[18px] text-white">
                    Reconnect
                  </span>
                </button>
              </div>
            </div>
          )}

          {!isVideoPromoDismissed && (
            <div className="px-3 pb-[10px]">
              <AiDashboardsHelpVideoCard
                variant="sidebar"
                onDismiss={dismissHelpVideoCard}
                onWatch={openHelpVideoModal}
              />
            </div>
          )}

          {/* ChatInput - all modes (disabled message in history mode) */}
          <ChatInput
            ref={chatInputRef}
            value={inputValue}
            isThinking={isThinking}
            isOpen={isOpen}
            disabled={
              isArchived || isAutoFixInFlight || isLoadingExistingThreads || isBuilderInputLocked
            }
            disabledMessage={
              isArchived
                ? "This dashboard is archived. Unarchive it to use AI chat."
                : isSuperAdminHistoryMode
                ? "History view is read-only"
                : undefined
            }
            showStop={isThinking && !isSuperAdminHistoryMode && !isBuilderMode}
            isStopDisabled={isPausing || postAgentThreadPauseMutation.isLoading}
            onStop={stopThread}
            onChange={setInputValue}
            onSubmit={handleSubmit}
            onWillChangeHeight={checkIfScrolledToBottom}
            onDidChangeHeight={handleInputHeightChanged}
            availableBlocks={availableBlocks}
            onBlockSelected={handleAddBlockContext}
            onPickerActivate={
              availableBlocks && availableBlocks.length > 0 ? activatePicker : undefined
            }
            onPickerDeactivate={isPickerActive ? deactivatePicker : undefined}
            targetDocument={targetDocument}
          />
        </aside>
        <DashboardHelpVideoModal
          open={isHelpVideoModalOpen}
          onClose={closeHelpVideoModal}
          portalDocument={targetDocument}
          videoUrl={getAiDashboardsHelpVideoUrl(sourceType)}
        />
        <UserMemoryDrawer
          open={isMemoryDrawerOpen}
          setOpen={setIsMemoryDrawerOpen}
          portalDocument={targetDocument}
        />
      </>
    );
  }
);

WebDashboardChatSidebar.displayName = "WebDashboardChatSidebar";
